import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { queryFor, useApiConfig } from "@visioni/a3s-client-api/app";
import { listFleetOperatorNotesEndpoint, type FleetOperatorNoteSummary } from "../../client-messages/operator-notes";
import { activationFiltersKey, type FleetActivationFilters } from "./activation-filters";
import { useCursorPager, useSkipEmptyPages } from "./cursor-pager";
import type { UseFleetListResult } from "./hooks";

const listFleetOperatorNotesQuery = queryFor(listFleetOperatorNotesEndpoint);

/** Rows per page of the operator note list. The endpoint caps at 100. */
export const OPERATOR_NOTES_PAGE_SIZE = 25;

/** Filtered exactly as the fleet activations are: a time window, and the start of a client number. */
export type FleetOperatorNoteFilters = FleetActivationFilters;

export type UseOperatorNotesResult = UseFleetListResult<FleetOperatorNoteSummary>;

export interface UseFleetOperatorNotesOptions {
  readonly filters: FleetOperatorNoteFilters;
  /** Gate the query — e.g. while the filters in the URL are invalid. */
  readonly enabled?: boolean;
}

/**
 * One page of the fleet's Patriot operator notes, newest first, ticketless
 * ones included.
 *
 * Paged like `useFleetActivations`, skipping empty pages the same way.
 * Changing any filter returns to page one.
 */
export function useFleetOperatorNotes({
  filters,
  enabled = true,
}: UseFleetOperatorNotesOptions): UseOperatorNotesResult {
  const apiConfig = useApiConfig();
  const pager = useCursorPager(activationFiltersKey(filters));

  const query = useQuery({
    ...listFleetOperatorNotesQuery(
      apiConfig,
      {},
      {
        limit: OPERATOR_NOTES_PAGE_SIZE,
        ...filters,
        ...(pager.cursor !== undefined && { cursor: pager.cursor }),
      },
    ),
    enabled,
    placeholderData: keepPreviousData,
  });
  const skipping = useSkipEmptyPages(pager, query.data, query.isPlaceholderData);

  return {
    items: skipping ? undefined : query.data?.items,
    clientNos: query.isPlaceholderData ? undefined : query.data?.clientNos,
    isLoading: query.isLoading || skipping,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: () => void query.refetch(),
    pager,
    // As in `useTenantActivations`: a placeholder's cursor belongs to the page being left.
    nextCursor: query.isPlaceholderData ? null : (query.data?.cursor ?? null),
  };
}
